import { useEffect, useState, useRef } from 'react';
import { Upload, Trash2, Image, Video, Copy, Check, Filter, FolderOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'all', label: 'Todas' },
  { value: 'general', label: 'General' },
  { value: 'tours', label: 'Tours' },
  { value: 'gallery', label: 'Galería' },
  { value: 'hero', label: 'Hero/Banner' },
];

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [copiedId, setCopiedId] = useState(null);
  
  const [uploadData, setUploadData] = useState({
    file: null,
    category: 'general',
    alt_text: ''
  });
  
  const fileInputRef = useRef(null);

  const fetchMedia = async () => {
    try {
      const category = categoryFilter === 'all' ? null : categoryFilter;
      const response = await adminApi.getMedia(category);
      setMedia(response.data);
    } catch (error) {
      toast.error('Error al cargar archivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [categoryFilter]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) return;
    
    setUploading(true);
    try {
      console.log('Iniciando subida de archivo:', uploadData.file.name, uploadData.file.size, 'bytes');
      const result = await adminApi.uploadMedia(uploadData.file, uploadData.category, uploadData.alt_text);
      console.log('Archivo subido exitosamente:', result.data);
      toast.success('Archivo subido exitosamente');
      setUploadDialogOpen(false);
      setUploadData({ file: null, category: 'general', alt_text: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchMedia();
    } catch (error) {
      console.error('Error al subir archivo:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Error al subir archivo';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    
    try {
      await adminApi.deleteMedia(deleteDialog.item.id);
      toast.success('Archivo eliminado');
      setDeleteDialog({ open: false, item: null });
      fetchMedia();
    } catch (error) {
      toast.error('Error al eliminar archivo');
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast.success('URL copiada al portapapeles');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isVideo = (mimeType) => mimeType?.startsWith('video/');

  return (
    <div data-testid="admin-media">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-stone-900">Galería de Medios</h1>
          <p className="text-stone-500 mt-1">Sube y gestiona imágenes y videos</p>
        </div>
        <Button 
          onClick={() => setUploadDialogOpen(true)} 
          className="rounded-xl gap-2"
          data-testid="upload-media-btn"
        >
          <Upload className="w-4 h-4" />
          Subir Archivo
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-stone-400" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-sm text-stone-500">
          {media.length} archivo{media.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="aspect-square bg-stone-100 rounded-xl skeleton" />
          ))}
        </div>
      ) : media.length === 0 ? (
        <Card className="p-12 text-center">
          <FolderOpen className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <p className="text-stone-500 mb-4">No hay archivos en esta categoría</p>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Subir primer archivo
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {media.map((item) => (
            <Card key={item.id} className="group relative overflow-hidden">
              <CardContent className="p-0">
                {/* Preview */}
                <div className="aspect-square bg-stone-100 relative">
                  {isVideo(item.mime_type) ? (
                    <div className="w-full h-full flex items-center justify-center bg-stone-800">
                      <Video className="w-12 h-12 text-white" />
                    </div>
                  ) : (
                    <img
                      src={`${BACKEND_URL}${item.url}`}
                      alt={item.alt_text || item.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="gap-1"
                      onClick={() => copyToClipboard(item.url)}
                    >
                      {copiedId === item.url ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      Copiar URL
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="gap-1"
                      onClick={() => setDeleteDialog({ open: true, item })}
                    >
                      <Trash2 className="w-3 h-3" />
                      Eliminar
                    </Button>
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-3">
                  <p className="text-sm font-medium text-stone-900 truncate" title={item.original_name}>
                    {item.original_name}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-stone-400">{formatFileSize(item.size)}</span>
                    <span className="text-xs px-2 py-0.5 bg-stone-100 rounded-full text-stone-600">
                      {item.category}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent aria-describedby="upload-dialog-description">
          <DialogHeader>
            <DialogTitle>Subir Archivo</DialogTitle>
            <p id="upload-dialog-description" className="text-sm text-stone-500">
              Selecciona un archivo para subir a la galería de medios.
            </p>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* File Input */}
            <div>
              <Label>Archivo</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/mp4"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  {uploadData.file ? (
                    <div className="text-center">
                      <Image className="w-8 h-8 mx-auto text-primary mb-2" />
                      <p className="text-sm font-medium text-stone-900">{uploadData.file.name}</p>
                      <p className="text-xs text-stone-500">{formatFileSize(uploadData.file.size)}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                      <p className="text-sm text-stone-500">Clic para seleccionar archivo</p>
                      <p className="text-xs text-stone-400">JPG, PNG, GIF, WEBP, MP4</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Category */}
            <div>
              <Label>Categoría</Label>
              <Select 
                value={uploadData.category} 
                onValueChange={(v) => setUploadData(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.value !== 'all').map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Alt Text */}
            <div>
              <Label>Texto alternativo (SEO)</Label>
              <Input
                value={uploadData.alt_text}
                onChange={(e) => setUploadData(prev => ({ ...prev, alt_text: e.target.value }))}
                placeholder="Descripción de la imagen"
                className="mt-2"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={!uploadData.file || uploading}
                className="flex-1"
              >
                {uploading ? 'Subiendo...' : 'Subir Archivo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este archivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El archivo "{deleteDialog.item?.original_name}" 
              será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
